"use client";

import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList
} from "@/components/shared/ui/command";
import { ShopEmailContact, ShopEmailContactList } from "@/lib/checkout/checkout-types";
import { Check, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Input } from "./input";

export type EmailInputProps = {
    // eslint-disable-next-line no-unused-vars
    onEmailsChange?: (contacts: ShopEmailContact[]) => void; // Optional callback for parent components (not serialized)
    name?: string; // For form integration
    defaultValue?: ShopEmailContact[]; // Initial value
    contactGroups: ShopEmailContactList[];
    placeholder?: string;
    disabled?: boolean;
    maxHeight?: string;
    className?: string;
    error?: string;
};

export function validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

export const EmailInput: React.FC<EmailInputProps> = ({
    onEmailsChange,
    defaultValue = [],
    contactGroups,
    placeholder = "Add email...",
    disabled = false,
    maxHeight = "200px",
    className = "",
    error
}) => {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [contacts, setContacts] = useState<ShopEmailContact[]>(defaultValue);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleAddEmail = (email: string) => {
        const trimmedEmail = email.trim();

        if (trimmedEmail === "" || contacts.some((contact) => contact.email === trimmedEmail)) {
            return;
        }

        if (validateEmail(trimmedEmail)) {
            let contact = contactGroups
                .find((group) => group.contacts.find((contact) => contact.email === trimmedEmail))
                ?.contacts.find((contact) => contact.email === trimmedEmail);

            if (!contact) {
                contact = { email: trimmedEmail, name: trimmedEmail, userID: 0 };
            }

            const newEmails = [...contacts, contact];

            setContacts(newEmails);
            setInputValue("");

            // Call callback if provided
            if (onEmailsChange) {
                onEmailsChange(newEmails);
            }
        }
    };

    const handleRemoveEmail = (emailToRemove: string) => {
        const newEmails = contacts.filter((contact) => contact.email !== emailToRemove);
        setContacts(newEmails);

        // Call callback if provided
        if (onEmailsChange) {
            onEmailsChange(newEmails);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && inputValue) {
            e.preventDefault();
            handleAddEmail(inputValue.trim());
        } else if ((e.key === "," || e.key === " ") && inputValue) {
            e.preventDefault();
            handleAddEmail(inputValue.replace(",", "").trim());
        }
    };

    // Focus the input when clicking on the container
    const handleContainerClick = () => {
        inputRef.current?.focus();
    };

    // Update dropdown open state when typing
    useEffect(() => {
        if (inputValue.length > 0 && !open) {
            setOpen(true);
        }

        if (!open && inputValue && !validateEmail(inputValue)) {
            setInputValue("");
        }
    }, [inputValue, open]);

    // Handle clicks outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Keep input focused when dropdown opens
    useEffect(() => {
        if (open) {
            // Use setTimeout to ensure focus happens after dropdown rendering
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        }
    }, [open]);

    return (
        <div className={`w-full ${className}`}>
            <div
                className={`flex flex-col gap-0 rounded-md border ${
                    error ? "border-red-500" : "border-input"
                } ${disabled ? "bg-muted" : "bg-background"}`}
                onClick={handleContainerClick}
            >
                <div className="flex flex-wrap justify-start p-0">
                    {contacts.map((contact) => (
                        <div
                            key={contact.email}
                            className="m-0.5 flex w-fit items-center gap-1 rounded-md bg-secondary px-2 py-1 text-secondary-foreground"
                        >
                            <span className="text-sm">{contact.email}</span>
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveEmail(contact.email);
                                    }}
                                    className="flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary-foreground/20"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                <div className="relative flex-1 p-0">
                    <Input
                        ref={inputRef}
                        type="email"
                        className="m-0 w-full flex-1 border-none p-0 pl-4 outline-none"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        disabled={disabled}
                        onFocus={() => {
                            setOpen(true);
                        }}
                    />

                    {open && (
                        <div
                            ref={dropdownRef}
                            className="absolute left-0 top-full z-50 w-full min-w-[240px] rounded-md border bg-popover p-0 shadow-md"
                        >
                            <Command>
                                <CommandList style={{ maxHeight }}>
                                    <CommandEmpty>
                                        Use <span className="underline">Space</span> or{" "}
                                        <span className="underline">Enter</span> to add a new email
                                    </CommandEmpty>
                                    {contactGroups.map((group) => {
                                        const filteredContacts = group.contacts.filter(
                                            (contact) => {
                                                const searchLower = inputValue.toLowerCase();
                                                return (
                                                    contact.email
                                                        .toLowerCase()
                                                        .includes(searchLower) ||
                                                    contact.name.toLowerCase().includes(searchLower)
                                                );
                                            }
                                        );

                                        if (filteredContacts.length === 0) {
                                            return null;
                                        } else if (filteredContacts.length === 0) {
                                            return (
                                                <CommandGroup
                                                    key={"new-email"}
                                                    heading={"New Email"}
                                                >
                                                    <CommandItem
                                                        key={inputValue}
                                                        onSelect={() => handleAddEmail(inputValue)}
                                                    >
                                                        <Check className="mr-2 h-4 w-4" />
                                                        <div className="flex flex-col">
                                                            <span>{inputValue}</span>
                                                            <span className="text-xs text-muted-foreground">
                                                                {inputValue}
                                                            </span>
                                                        </div>
                                                    </CommandItem>
                                                </CommandGroup>
                                            );
                                        }

                                        return (
                                            <CommandGroup key={group.label} heading={group.label}>
                                                {filteredContacts.map((contact) => (
                                                    <CommandItem
                                                        key={contact.userID}
                                                        value={contact.email}
                                                        onSelect={() => {
                                                            handleAddEmail(contact.email);
                                                        }}
                                                    >
                                                        <Check
                                                            className={`mr-2 h-4 w-4 ${
                                                                contacts.some(
                                                                    (email) =>
                                                                        email.email ===
                                                                        contact.email
                                                                )
                                                                    ? "opacity-100"
                                                                    : "opacity-0"
                                                            }`}
                                                        />
                                                        <div className="flex flex-col">
                                                            <span>{contact.name}</span>
                                                            <span className="text-xs text-muted-foreground">
                                                                {contact.email}
                                                            </span>
                                                        </div>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        );
                                    })}
                                </CommandList>
                            </Command>
                        </div>
                    )}
                </div>
            </div>

            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </div>
    );
};
